import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Shield, RefreshCw, Download } from 'lucide-react';
import { BACKEND_URL } from '../../utils/backendURL';

const PAGE_SIZE = 50;
const FILTERS = [['all', 'All Events'], ['critical', 'Critical Only'], ['warning', 'Warnings Only'],
  ['info', 'Info Only'], ['failed_login', 'Failed Logins'], ['admin_login', 'Admin Logins'],
  ['plan_change', 'Plan Changes'], ['admin_action', 'Other Audit Events']];
const SEVERITIES = { info: 'bg-blue-100 text-blue-800', warning: 'bg-yellow-100 text-yellow-800',
  critical: 'bg-red-100 text-red-800' };

export default function SecurityLogs() {
  const [period, setPeriod] = useState('24h');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setData(null);
    setError('');
    const timer = setTimeout(async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/ops/security/logs`, {
          params: { period, event_filter: filter, search, skip: page * PAGE_SIZE, limit: PAGE_SIZE },
          headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
          signal: controller.signal,
        });
        if (!controller.signal.aborted) setData(response.data);
      } catch (failure) {
        if (!controller.signal.aborted) setError(failure.response?.status === 403
          ? 'Your OPS role cannot view security logs.' : 'Unable to load security logs. Please retry.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [period, filter, search, page, revision]);

  const change = (setter) => (event) => { setter(event.target.value); setPage(0); };
  const exportPage = () => {
    const payload = { exported_at: new Date().toISOString(), period, filter, search,
      page: page + 1, total_matching: data.counts.total, events: data.items };
    const url = URL.createObjectURL(new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `lexa-security-logs-page-${page + 1}.json`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const counts = data?.counts;

  return (
    <section className="space-y-6" aria-label="Security logs">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Security &amp; Audit Logs</h1>
          <p className="text-gray-600">Recorded OPS logins and existing administrative audit events</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={() => setRevision((value) => value + 1)} disabled={loading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </button>
          <button onClick={exportPage} disabled={loading || !data?.items.length}
            className="flex items-center px-4 py-2 border rounded-lg disabled:opacity-50">
            <Download className="w-4 h-4 mr-2" />Export this page
          </button>
        </div>
      </div>
      <p className="text-sm text-gray-600 bg-blue-50 rounded-lg p-4">
        This view shows recorded events, not a security scan. Login tracking starts with this release;
        earlier login attempts are not available. Some actions do not yet produce audit records.
        IP addresses identify the connection peer and may belong to a reverse proxy.
      </p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {['total', 'critical', 'warning', 'failed_logins'].map((key, index) => (
          <div key={key} className="admin-card p-5">
            <p className="text-sm text-gray-600">{['Matching Events', 'Critical Events', 'Warnings', 'Failed OPS Logins'][index]}</p>
            <p className="text-3xl font-bold mt-1">{counts?.[key] ?? '—'}</p>
          </div>
        ))}
      </div>
      <div className="admin-card p-4 flex flex-wrap gap-3">
        <input aria-label="Search security logs" placeholder="Search by action, operator, target or IP..."
          value={search} onChange={change(setSearch)} maxLength={120}
          className="flex-1 min-w-0 basis-60 border rounded-lg px-3 py-2" />
        <select aria-label="Event filter" value={filter} onChange={change(setFilter)} className="border rounded-lg px-3 py-2">
          {FILTERS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <select aria-label="Time range" value={period} onChange={change(setPeriod)} className="border rounded-lg px-3 py-2">
          {[['1h', 'Last Hour'], ['24h', 'Last 24 Hours'], ['7d', 'Last 7 Days'], ['30d', 'Last 30 Days']]
            .map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>
      {loading && <p role="status">Loading security logs...</p>}
      {error && <div role="alert" className="text-red-700 border border-red-200 p-4 rounded-lg">
        {error} <button className="underline ml-2" onClick={() => setRevision((value) => value + 1)}>Retry</button>
      </div>}
      {!loading && data && <>
        <div className="admin-card overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50"><tr>
              {['Event', 'Operator', 'Connection IP', 'Severity', 'Time', 'Details'].map((title) =>
                <th key={title} className="px-4 py-3 text-left font-medium text-gray-600">{title}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {data.items.map((log) => <tr key={log.id}>
                <td className="px-4 py-4"><p className="font-medium">{log.action}</p><p className="text-gray-500">{log.type.replaceAll('_', ' ')}</p></td>
                <td className="px-4 py-4 break-all">{log.user}</td>
                <td className="px-4 py-4 font-mono">{log.ip_address || 'Unavailable'}</td>
                <td className="px-4 py-4"><span className={`px-2 py-1 rounded-full text-xs ${SEVERITIES[log.severity] || SEVERITIES.info}`}>{log.severity.toUpperCase()}</span></td>
                <td className="px-4 py-4 whitespace-nowrap">{new Date(log.timestamp).toLocaleString()}</td>
                <td className="px-4 py-4"><dl className="max-w-xs break-words">
                  {Object.entries(log.details).map(([key, value]) => <div key={key}>
                    <dt className="inline font-medium">{key.replaceAll('_', ' ')}: </dt><dd className="inline">{String(value)}</dd>
                  </div>)}
                </dl></td>
              </tr>)}
              {!data.items.length && <tr><td colSpan={6} className="p-8 text-center text-gray-500">
                <Shield className="w-8 h-8 mx-auto mb-2" />No recorded events match these filters.
              </td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap justify-between items-center gap-3 text-sm">
          <p>{counts.total ? `${page * PAGE_SIZE + 1}–${page * PAGE_SIZE + data.items.length}` : '0'} of {counts.total} matching events</p>
          <div className="flex items-center gap-3">
            <button disabled={page === 0} onClick={() => setPage((value) => value - 1)} className="border rounded px-3 py-2 disabled:opacity-40">Previous</button>
            <span>Page {page + 1}</span>
            <button disabled={(page + 1) * PAGE_SIZE >= counts.total} onClick={() => setPage((value) => value + 1)} className="border rounded px-3 py-2 disabled:opacity-40">Next</button>
          </div>
        </div>
      </>}
    </section>
  );
}
